import { ApplicationStatusSubscriptionState } from './application-status-subscription-selector';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';
import {
  Action,
  deleteResourceByParam,
  setStateDirtyByParam,
  updateResourceByParam,
} from '../reducers/utils/state-manager';

import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: ApplicationStatusSubscriptionState = {
  applicationStatusSubscriptionsById: {},
  applicationStatusSubscriptionsStateById: {},
  applicationStatusSubscriptionsStateByJobId: {},
  applicationStatusSubscriptionsStateByOrgId: {},
};

const idFieldAndValue = ({ jobId, orgId }: OneIdAllowedConstraint) => {
  if (typeof jobId === 'number') {
    return {
      id: 'jobId',
      value: jobId,
    };
  }

  return {
    id: 'orgId',
    value: orgId,
  };
};

export const applicationStatusSubscriptionReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('CREATE_APPLICATION_STATUS_SUBSCRIPTION'):
    case STARTING('LOAD_APPLICATION_STATUS_SUBSCRIPTIONS'): {
      const { jobId, orgId } = action.payload.args;
      const { id, value } = idFieldAndValue({ jobId, orgId });
      return updateResourceByParam(
        state,
        'applicationStatusSubscriptions',
        id,
        String(value),
        true,
      );
    }
    case SUCCESS('LOAD_APPLICATION_STATUS_SUBSCRIPTIONS'): {
      const { jobId, orgId } = action.payload.args;
      const { id, value } = idFieldAndValue({ jobId, orgId });
      const { data }: { data: ApplicationStatusSubscription[] } = action.payload;
      return updateResourceByParam(
        state,
        'applicationStatusSubscriptions',
        id,
        String(value),
        false,
        convertArrayToMap(data, ({ applicationStatusSubscriptionId }) =>
          String(applicationStatusSubscriptionId),
        ),
      );
    }
    case FAILURE('LOAD_APPLICATION_STATUS_SUBSCRIPTIONS'): {
      const { jobId, orgId } = action.payload.args;
      const { id, value } = idFieldAndValue({ jobId, orgId });

      return updateResourceByParam(
        state,
        'applicationStatusSubscriptions',
        id,
        String(value),
        false,
      );
    }
    case SUCCESS('CREATE_APPLICATION_STATUS_SUBSCRIPTION'): {
      const { jobId, orgId } = action.payload.args;
      const { id, value } = idFieldAndValue({ jobId, orgId });

      const { data }: { data: ApplicationStatusSubscription } = action.payload;

      return updateResourceByParam(
        state,
        'applicationStatusSubscriptions',
        id,
        String(value),
        false,
        {
          [data.applicationStatusSubscriptionId]: data,
        },
      );
    }
    case STARTING('DELETE_APPLICATION_STATUS_SUBSCRIPTION'): {
      const { applicationStatusSubscriptionId } = action.payload.args;
      return updateResourceByParam(
        state,
        'applicationStatusSubscriptions',
        'id',
        applicationStatusSubscriptionId,
        true,
      );
    }
    case SUCCESS('DELETE_APPLICATION_STATUS_SUBSCRIPTION'): {
      const { applicationStatusSubscriptionId } = action.payload.args;
      return deleteResourceByParam(
        state,
        'applicationStatusSubscriptions',
        'id',
        applicationStatusSubscriptionId,
        applicationStatusSubscriptionId,
      );
    }
    case SUCCESS('UPDATE_MANY_APPLICATION_STATUS_SUBSCRIPTION'): {
      const { jobId, orgId } = action.payload.args;
      const { deletedSubscriptionIds } = action.payload.data;

      let newState = { ...state };
      // Reloading items doesn't do an exact replacement,
      // so items that were deleted will remain in state unless we remove them here
      if (deletedSubscriptionIds) {
        deletedSubscriptionIds.forEach((deletedId: number) => {
          delete newState.applicationStatusSubscriptionsById[deletedId];
        });
      }

      // Ideally, we update state with the created subscriptions since we have them here, but the
      // subscription component used in the job redux form is not reloading properly when we do that,
      // so for now we will just set dirty, but in the future this can be changed.
      newState = setStateDirtyByParam(newState, 'applicationStatusSubscriptions', 'orgId', orgId);
      return jobId
        ? setStateDirtyByParam(newState, 'applicationStatusSubscriptions', 'jobId', jobId)
        : newState;
    }
    default: {
      return state;
    }
  }
};
